import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { getTenants, updateTenant, exportTenantConversations } from '../../services/apiSuperAdmin';
import TenantModal from '../../components/super-admin/TenantModal';
import TenantStatsDrawer from '../../components/super-admin/TenantStatsDrawer';
import CloneTenantModal from '../../components/super-admin/CloneTenantModal';
import TenantModulesModal from '../../components/super-admin/TenantModulesModal';
import ForceDeleteModal from '../../components/super-admin/ForceDeleteModal';
import OpenRouterConfigModal from '../../components/super-admin/OpenRouterConfigModal';

const STATUS_COLORS = {
    true:  'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400',
    false: 'bg-slate-100 text-slate-500 dark:bg-slate-800 dark:text-slate-400',
};

export default function TenantsPage() {
    const qc = useQueryClient();
    const [modal, setModal] = useState({ open: false, tenant: null });
    const [statsDrawer, setStatsDrawer] = useState({ open: false, tenant: null });
    const [cloneModal, setCloneModal] = useState({ open: false, tenant: null });
    const [modulesModal, setModulesModal] = useState({ open: false, tenant: null });
    const [forceDelete, setForceDelete] = useState({ open: false, tenant: null });
    const [downloadingId, setDownloadingId] = useState(null);
    const [openRouterModalOpen, setOpenRouterModalOpen] = useState(false);

    const handleDownload = async (tenant) => {
        try {
            setDownloadingId(tenant.id);
            const data = await exportTenantConversations(tenant.id);
            const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = `conversations_tenant_${tenant.slug}.json`;
            document.body.appendChild(a);
            a.click();
            a.remove();
            window.URL.revokeObjectURL(url);
        } catch (error) {
            console.error('Error exporting conversations:', error);
            alert('Error al exportar conversaciones. Inténtalo de nuevo.');
        } finally {
            setDownloadingId(null);
        }
    };

    const { data: tenantsRaw, isLoading } = useQuery({
        queryKey: ['super-admin-tenants'],
        queryFn: getTenants,
    });
    const tenants = Array.isArray(tenantsRaw) ? tenantsRaw : [];

    const toggleActiveMut = useMutation({
        mutationFn: ({ id, is_active }) => updateTenant({ id, is_active }),
        onSuccess: () => qc.invalidateQueries({ queryKey: ['super-admin-tenants'] }),
    });

    const openCreate = () => setModal({ open: true, tenant: null });
    const openEdit = (t) => setModal({ open: true, tenant: t });
    const closeModal = () => setModal({ open: false, tenant: null });

    // Summary Stats
    const totalTenants  = tenants.length;
    const activeTenants = tenants.filter(t => t.is_active).length;
    const totalUsers    = tenants.reduce((s, t) => s + (t.users_count ?? 0), 0);
    const totalClones   = tenants.filter(t => t.cloned_from_tenant_id).length;

    return (
        <div className="flex-1 overflow-auto bg-slate-50 dark:bg-background-dark">
            {/* Header */}
            <div className="sticky top-0 z-10 bg-white dark:bg-surface-dark border-b border-gray-100 dark:border-gray-800 px-8 py-5">
                <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-xl bg-violet-100 dark:bg-violet-900/40 flex items-center justify-center">
                            <span className="material-symbols-outlined text-violet-600 dark:text-violet-400 text-[22px]">
                                hub
                            </span>
                        </div>
                        <div>
                            <h1 className="text-xl font-bold text-slate-900 dark:text-slate-100">
                                Administración Global
                            </h1>
                            <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">
                                Super Admin · Gestión de Tenants
                            </p>
                        </div>
                    </div>
                    <div className="flex items-center gap-3">
                        <button
                            onClick={() => setOpenRouterModalOpen(true)}
                            className="flex items-center gap-2 px-4 py-2.5 bg-purple-100 hover:bg-purple-200 dark:bg-purple-950/60 dark:hover:bg-purple-900/60 text-purple-700 dark:text-purple-300 font-semibold rounded-xl transition-all text-sm border border-purple-200 dark:border-purple-800 shadow-sm"
                        >
                            <span className="material-symbols-outlined text-[18px]">auto_awesome</span>
                            Configurar OpenRouter API
                        </button>
                        <button
                            id="btn-new-tenant"
                            onClick={openCreate}
                            className="flex items-center gap-2 px-5 py-2.5 bg-violet-600 hover:bg-violet-700 text-white font-semibold rounded-xl shadow-lg shadow-violet-500/25 transition-all text-sm"
                        >
                            <span className="material-symbols-outlined text-[18px]">add_business</span>
                            Nuevo Tenant
                        </button>
                    </div>
                </div>
            </div>

            <div className="px-8 py-6 space-y-6">
                {/* Summary Cards */}
                <div className="grid grid-cols-4 gap-5">
                    <StatCard icon="domain" label="Total Tenants" value={totalTenants} color="violet" />
                    <StatCard icon="check_circle" label="Activos" value={activeTenants} color="emerald" />
                    <StatCard icon="people" label="Total Usuarios" value={totalUsers} color="sky" />
                    <StatCard icon="content_copy" label="Clones" value={totalClones} color="amber" />
                </div>

                {/* Tenants Table */}
                {isLoading ? (
                    <div className="flex items-center justify-center py-24">
                        <div className="w-9 h-9 rounded-full border-4 border-violet-200 border-t-violet-600 animate-spin" />
                    </div>
                ) : tenants.length === 0 ? (
                    <EmptyState onNew={openCreate} />
                ) : (
                    <div className="bg-white dark:bg-surface-dark rounded-2xl border border-gray-100 dark:border-gray-800 overflow-hidden shadow-sm">
                        <table className="w-full">
                            <thead>
                                <tr className="bg-slate-50 dark:bg-slate-800/60 text-[11px] font-bold uppercase tracking-wider text-slate-400 border-b border-gray-100 dark:border-gray-800">
                                    <th className="py-3.5 pl-6 pr-3 text-left w-16">ID</th>
                                    <th className="py-3.5 px-3 text-left">Organización</th>
                                    <th className="py-3.5 px-3 text-left">Manager</th>
                                    <th className="py-3.5 px-3 text-center">Usuarios</th>
                                    <th className="py-3.5 px-3 text-center">Estado</th>
                                    <th className="py-3.5 pl-3 pr-6 text-right">Acciones</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-50 dark:divide-gray-800">
                                {tenants.map(tenant => {
                                    const manager = tenant.users?.[0];
                                    return (
                                        <tr
                                            key={tenant.id}
                                            className={`hover:bg-slate-50 dark:hover:bg-slate-800/40 transition-colors ${!tenant.is_active ? 'opacity-60' : ''}`}
                                        >
                                            {/* ID */}
                                            <td className="py-4 pl-6 pr-3">
                                                <span className="text-xs font-bold text-slate-400 font-mono bg-slate-100 dark:bg-slate-800 px-2 py-1 rounded">
                                                    #{tenant.id}
                                                </span>
                                            </td>

                                            {/* Name + Slug */}
                                            <td className="py-4 px-3">
                                                <div className="flex items-center gap-3">
                                                    <TenantAvatar name={tenant.name} />
                                                    <div>
                                                        <div className="flex items-center gap-2">
                                                            <p className="font-semibold text-sm text-slate-800 dark:text-slate-100">
                                                                {tenant.name}
                                                            </p>
                                                            {tenant.cloned_from_tenant_id && (
                                                                <span className="inline-flex items-center gap-1 text-[10px] font-bold px-1.5 py-0.5 rounded-full bg-violet-100 text-violet-700 dark:bg-violet-900/40 dark:text-violet-300">
                                                                    <span className="material-symbols-outlined text-[11px]">content_copy</span>
                                                                    CLON
                                                                </span>
                                                            )}
                                                            {tenant.clones_count > 0 && (
                                                                <span className="inline-flex items-center gap-1 text-[10px] font-bold px-1.5 py-0.5 rounded-full bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300" title={`${tenant.clones_count} clon(es) activos`}>
                                                                    <span className="material-symbols-outlined text-[11px]">hub</span>
                                                                    {tenant.clones_count} clon{tenant.clones_count !== 1 ? 'es' : ''}
                                                                </span>
                                                            )}
                                                        </div>
                                                        <p className="text-xs text-slate-400 font-mono mt-0.5">
                                                            /{tenant.slug}
                                                        </p>
                                                    </div>
                                                </div>
                                            </td>

                                            {/* Manager */}
                                            <td className="py-4 px-3">
                                                {manager ? (
                                                    <div>
                                                        <p className="text-sm font-medium text-slate-700 dark:text-slate-300">
                                                            {manager.name}
                                                        </p>
                                                        <p className="text-xs text-slate-400">{manager.email}</p>
                                                    </div>
                                                ) : (
                                                    <span className="text-xs text-slate-400 italic">Sin manager</span>
                                                )}
                                            </td>

                                            {/* Users Count */}
                                            <td className="py-4 px-3 text-center">
                                                <span className="inline-flex items-center justify-center w-8 h-8 rounded-full bg-slate-100 dark:bg-slate-800 text-sm font-bold text-slate-600 dark:text-slate-300">
                                                    {tenant.users_count}
                                                </span>
                                            </td>

                                            {/* Status */}
                                            <td className="py-4 px-3 text-center">
                                                <span className={`inline-flex items-center gap-1.5 text-xs font-semibold px-2.5 py-1 rounded-full ${STATUS_COLORS[tenant.is_active]}`}>
                                                    <span className={`w-1.5 h-1.5 rounded-full ${tenant.is_active ? 'bg-emerald-500' : 'bg-slate-400'}`} />
                                                    {tenant.is_active ? 'Activo' : 'Inactivo'}
                                                </span>
                                            </td>

                                            {/* Actions */}
                                            <td className="py-4 pl-3 pr-6">
                                                <div className="flex items-center justify-end gap-1">
                                                    <ActionBtn
                                                        icon="visibility"
                                                        title="Ver Estadísticas"
                                                        color="sky"
                                                        onClick={() => setStatsDrawer({ open: true, tenant })}
                                                    />
                                                    <ActionBtn
                                                        icon="download"
                                                        title="Descargar conversaciones de Leads"
                                                        color="emerald"
                                                        onClick={() => handleDownload(tenant)}
                                                        loading={downloadingId === tenant.id}
                                                    />
                                                    <ActionBtn
                                                        icon="widgets"
                                                        title="Módulos y funciones"
                                                        color="violet"
                                                        onClick={() => setModulesModal({ open: true, tenant })}
                                                    />
                                                    <ActionBtn
                                                        icon="content_copy"
                                                        title="Clonar Tenant"
                                                        color="violet"
                                                        onClick={() => setCloneModal({ open: true, tenant })}
                                                    />
                                                    <ActionBtn
                                                        icon="edit"
                                                        title="Editar"
                                                        color="primary"
                                                        onClick={() => openEdit(tenant)}
                                                    />
                                                    <ActionBtn
                                                        icon={tenant.is_active ? 'pause_circle' : 'play_circle'}
                                                        title={tenant.is_active ? 'Desactivar' : 'Activar'}
                                                        color={tenant.is_active ? 'amber' : 'emerald'}
                                                        onClick={() => toggleActiveMut.mutate({ id: tenant.id, is_active: !tenant.is_active })}
                                                        loading={toggleActiveMut.isPending && toggleActiveMut.variables?.id === tenant.id}
                                                    />
                                                    <ActionBtn
                                                        icon="delete_forever"
                                                        title="Eliminar"
                                                        color="red"
                                                        onClick={() => setForceDelete({ open: true, tenant })}
                                                    />
                                                </div>
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>

            {/* Create/Edit Modal */}
            <TenantModal
                isOpen={modal.open}
                onClose={closeModal}
                tenant={modal.tenant}
            />

            {/* Módulos y funciones */}
            <TenantModulesModal
                isOpen={modulesModal.open}
                onClose={() => setModulesModal({ open: false, tenant: null })}
                tenant={modulesModal.tenant}
            />

            {/* Clone Modal */}
            <CloneTenantModal
                isOpen={cloneModal.open}
                onClose={() => setCloneModal({ open: false, tenant: null })}
                tenant={cloneModal.tenant}
            />

            {/* Force Delete Modal */}
            <ForceDeleteModal
                isOpen={forceDelete.open}
                onClose={() => setForceDelete({ open: false, tenant: null })}
                tenant={forceDelete.tenant}
            />

            {/* Stats Drawer */}
            <TenantStatsDrawer
                isOpen={statsDrawer.open}
                onClose={() => setStatsDrawer({ open: false, tenant: null })}
                tenant={statsDrawer.tenant}
            />

            {/* Global OpenRouter API Key Modal */}
            <OpenRouterConfigModal
                isOpen={openRouterModalOpen}
                onClose={() => setOpenRouterModalOpen(false)}
            />
        </div>
    );
}

// ── Sub-components ──

function StatCard({ icon, label, value, color }) {
    const colors = {
        violet:  'bg-violet-50 dark:bg-violet-900/20 text-violet-600 dark:text-violet-400',
        emerald: 'bg-emerald-50 dark:bg-emerald-900/20 text-emerald-600 dark:text-emerald-400',
        sky:     'bg-sky-50 dark:bg-sky-900/20 text-sky-600 dark:text-sky-400',
        amber:   'bg-amber-50 dark:bg-amber-900/20 text-amber-600 dark:text-amber-400',
    };
    return (
        <div className="bg-white dark:bg-surface-dark rounded-2xl border border-gray-100 dark:border-gray-800 shadow-sm p-5">
            <div className="flex items-start justify-between">
                <div>
                    <p className="text-xs font-semibold text-slate-400 uppercase tracking-wider">{label}</p>
                    <p className="text-3xl font-bold text-slate-900 dark:text-slate-100 mt-1">{value}</p>
                </div>
                <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${colors[color]}`}>
                    <span className="material-symbols-outlined text-[22px]">{icon}</span>
                </div>
            </div>
        </div>
    );
}

function TenantAvatar({ name }) {
    const initials = name.split(' ').map(w => w[0]).slice(0, 2).join('').toUpperCase();
    const hue = name.split('').reduce((s, c) => s + c.charCodeAt(0), 0) % 360;
    return (
        <div
            className="w-10 h-10 rounded-xl flex items-center justify-center text-white text-sm font-bold shrink-0"
            style={{ background: `hsl(${hue}, 65%, 55%)` }}
        >
            {initials}
        </div>
    );
}

function ActionBtn({ icon, title, color, onClick, loading, disabled }) {
    const colors = {
        primary: 'hover:text-violet-600 hover:bg-violet-50 dark:hover:bg-violet-900/20',
        violet:  'hover:text-violet-600 hover:bg-violet-50 dark:hover:bg-violet-900/20',
        sky:     'hover:text-sky-600 hover:bg-sky-50 dark:hover:bg-sky-900/20',
        amber:   'hover:text-amber-600 hover:bg-amber-50 dark:hover:bg-amber-900/20',
        emerald: 'hover:text-emerald-600 hover:bg-emerald-50 dark:hover:bg-emerald-900/20',
        red:     'hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20',
    };
    return (
        <button
            title={title}
            onClick={onClick}
            disabled={loading || disabled}
            className={`p-1.5 rounded-lg transition-colors text-slate-400 disabled:opacity-30 disabled:cursor-not-allowed ${colors[color] ?? ''}`}
        >
            {loading
                ? <span className="w-[18px] h-[18px] border-2 border-current/30 border-t-current rounded-full animate-spin block" />
                : <span className="material-symbols-outlined text-[18px]">{icon}</span>
            }
        </button>
    );
}

function EmptyState({ onNew }) {
    return (
        <div className="flex flex-col items-center justify-center py-24 text-center">
            <div className="w-20 h-20 rounded-2xl bg-violet-100 dark:bg-violet-900/30 flex items-center justify-center mb-5">
                <span className="material-symbols-outlined text-violet-400 text-5xl">domain</span>
            </div>
            <h3 className="text-lg font-bold text-slate-700 dark:text-slate-300">Sin tenants aún</h3>
            <p className="text-sm text-slate-400 mt-1 mb-6 max-w-xs">
                Crea tu primera organización para que los managers puedan configurar su entorno y vendedores.
            </p>
            <button
                onClick={onNew}
                className="flex items-center gap-2 px-5 py-2.5 bg-violet-600 hover:bg-violet-700 text-white font-semibold rounded-xl text-sm shadow-lg shadow-violet-500/20 transition-all"
            >
                <span className="material-symbols-outlined text-[18px]">add_business</span>
                Crear primer Tenant
            </button>
        </div>
    );
}

function DeleteConfirm({ tenant, loading, onCancel, onConfirm }) {
    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
            <div className="bg-white dark:bg-slate-900 rounded-2xl shadow-2xl w-full max-w-sm p-7 text-center">
                <div className="w-16 h-16 rounded-2xl bg-red-100 dark:bg-red-900/30 flex items-center justify-center mx-auto mb-4">
                    <span className="material-symbols-outlined text-red-500 text-4xl">warning</span>
                </div>
                <h3 className="text-lg font-bold text-slate-800 dark:text-slate-100">¿Eliminar Tenant?</h3>
                <p className="text-sm text-slate-500 dark:text-slate-400 mt-2 mb-6">
                    Estás a punto de eliminar <strong className="text-slate-700 dark:text-slate-300">{tenant.name}</strong> permanentemente.
                    Esta acción no se puede deshacer.
                </p>
                <div className="flex gap-3">
                    <button
                        onClick={onCancel}
                        className="flex-1 py-2.5 rounded-xl text-sm font-medium border border-gray-200 dark:border-slate-700 text-slate-600 dark:text-slate-400 hover:bg-slate-50 dark:hover:bg-slate-800 transition-colors"
                    >
                        Cancelar
                    </button>
                    <button
                        onClick={onConfirm}
                        disabled={loading}
                        className="flex-1 py-2.5 rounded-xl text-sm font-bold bg-red-600 hover:bg-red-700 disabled:bg-red-400 text-white shadow-lg shadow-red-500/20 transition-all flex items-center justify-center gap-2"
                    >
                        {loading
                            ? <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                            : 'Eliminar definitivamente'
                        }
                    </button>
                </div>
            </div>
        </div>
    );
}
